/* Decorative SVG; title intentionally omitted */
import type { Side } from '@/store/targetAccumulator/state.ts';
import { getTheme } from '@/utils/theme.ts';

interface GraphTheme {
  generalColor: string;
  dashLineColor: string;
}

const darkTheme: GraphTheme = {
  dashLineColor: '#FFFFFF',
  generalColor: '#DDDDDD',
};

const lightTheme: GraphTheme = {
  dashLineColor: '#5e5e5e',
  generalColor: '#000000',
};

interface PayoffGraphBaseProps {
  productName: 'FxTargetAccumulator' | 'FxForwardAccumulator';
  way: Side;
  isAccumulatorTrigger: boolean;
}

interface PayoffGraphAllProps extends Pick<PayoffGraphBaseProps, 'way' | 'isAccumulatorTrigger'> {
  theme: GraphTheme;
}

type PayoffGraphProps = Pick<PayoffGraphAllProps, 'way' | 'theme'>;

export const AccumulatorPayoffGraph = ({ way, productName, isAccumulatorTrigger }: PayoffGraphBaseProps) => {
  const theme = getTheme();
  const graphTheme = theme === 'dark' ? darkTheme : lightTheme;
  const themeColor = graphTheme.generalColor;

  const graphProps = { way, theme: graphTheme, isAccumulatorTrigger };

  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 273 210" xmlSpace="preserve">
      <StyledSvgPathGroup id="base" fill={themeColor}>
        <path d="M10.4,5.8l-2.3,5.1L5.8,16h3.8v65.2v12.1v86.6H5.8l2.3,5.1l2.3,5.1l2.3-5.1l2.3-5.1h-3.8V95.5h228v3.8l5.1-2.3l5.1-2.3l-5.1-2.3l-5.1-2.3v3.8h-228v-0.6V81.2V16H15l-2.3-5.1L10.4,5.8z" />
        <text className="fw-medium" style={{ fontSize: '.76rem' }} fill={themeColor} x="20" y="18">
          Profit at expiry
        </text>
        <text className="fw-medium" style={{ fontSize: '.76rem' }} fill={themeColor} x="20" y="180">
          Loss
        </text>
        <text className="fw-medium" style={{ fontSize: '.76rem' }} fill={themeColor} x="256" y="99">
          S
        </text>
        <text className="fw-bold" style={{ fontSize: '.66rem' }} fill={themeColor} x="68" y="190">
          Payoff profile at a fixing date
        </text>
        <text className="fw-bold" style={{ fontSize: '.66rem' }} fill={themeColor} x="14" y="204">
          For a leveraged version of a Target Accumulator
        </text>
      </StyledSvgPathGroup>
      {productName === 'FxTargetAccumulator' ? (
        <PayoffGraphAllTarf {...graphProps} />
      ) : (
        <PayoffGraphAllFwac {...graphProps} />
      )}
    </svg>
  );
};

const PayoffGraphAllTarf = ({ isAccumulatorTrigger, way, theme }: PayoffGraphAllProps) => {
  const graphProps = { way, theme };

  return <>{!isAccumulatorTrigger ? <GraphTarf {...graphProps} /> : <GraphTarfWithEki {...graphProps} />}</>;
};

function GraphTarf({ way, theme }: PayoffGraphProps) {
  const themeColor = theme.generalColor;
  const themeDashColor = theme.dashLineColor;

  return (
    <>
      {way === 'Buy' ? (
        <StyledSvgPathGroup id="target-buy" fill={themeColor}>
          <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="32" y="38">
            Target
          </text>
          <line stroke={themeDashColor} strokeDasharray="3 0 3" x1="36.7" y1="43.7" x2="201" y2="43.7" />
          <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="80" y="79">
            K
          </text>
          <rect fill={themeColor} width="1.8" height="14.1" x="84" y="87.3" />

          <text
            className="fw-medium"
            fontStyle="italic"
            style={{ fontSize: '.68rem' }}
            transform="translate(-70, 80) rotate(-46)"
            x="50"
            y="162"
          >
            Outright Fwd
          </text>
          <line stroke={themeDashColor} strokeDasharray="3 0 3" x1="48" y1="164" x2="188" y2="26" />

          <StyledSvgPath stroke="#FF0000" strokeWidth={2} d="m52.3 173.9 32.3-79.1 51-51 .2-.2 90-0" />
        </StyledSvgPathGroup>
      ) : (
        <StyledSvgPathGroup id="target-sell" fill={themeColor}>
          <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="216" y="38">
            Target
          </text>
          <line stroke={theme.dashLineColor} strokeDasharray="3 0 3" x1="62" y1="43.7" x2="248" y2="43.7" />

          <text
            className="fw-medium"
            fontStyle="italic"
            style={{ fontSize: '.68rem' }}
            transform="translate(200, -40) rotate(46)"
            x="50"
            y="162"
          >
            Outright Fwd
          </text>
          <line stroke={theme.dashLineColor} strokeDasharray="3 0 3" x1="54" y1="26" x2="196" y2="164" />

          <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="155" y="79">
            K
          </text>
          <rect fill={themeColor} width="1.8" height="14.1" x="158" y="87.3" />
          <StyledSvgPath stroke="#FF0000" strokeWidth={2} d="m191.7 174 -32.3-79.1 -51-51 -.2-.2 -80-0" />
        </StyledSvgPathGroup>
      )}
    </>
  );
}

function GraphTarfWithEki({ way, theme }: PayoffGraphProps) {
  const themeColor = theme.generalColor;

  return (
    <>
      {way === 'Buy' ? (
        <StyledSvgPathGroup id="target-eki-buy" fill={themeColor}>
          <text
            className="fw-medium"
            fontStyle="italic"
            style={{ fontSize: '.68rem' }}
            transform="translate(-66, 46) rotate(-29)"
            x="50"
            y="162"
          >
            Outright Forward
          </text>
          <StyledSvgPath stroke={theme.dashLineColor} strokeDasharray="dash" d="M43.3,149.6L234.6,43.3" />
          <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="32" y="38">
            Target
          </text>
          <line stroke={theme.dashLineColor} strokeDasharray="3 0 3" x1="36.7" y1="43.7" x2="201" y2="43.7" />
          <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="55" y="79">
            K
          </text>
          <rect fill={themeColor} width="1.8" height="14.1" x="57" y="87.3" />
          <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="90" y="79">
            B
          </text>
          <rect fill={themeColor} width="1.8" height="14.1" x="92" y="87.3" />
          <StyledSvgPath stroke="#FF0000" strokeWidth={2} d="M26.1,159.5l32.2-30.8l0.2-34.1h34.5l98.8-50.8h49.5" />
        </StyledSvgPathGroup>
      ) : (
        <StyledSvgPathGroup id="target-eki-sell" fill={themeColor}>
          <StyledSvgPath stroke={theme.dashLineColor} strokeDasharray="dash" d="M223.3,149.6L31.9,43.3" />
          <text
            className="fw-medium"
            fontStyle="italic"
            style={{ fontSize: '.68rem' }}
            transform="translate(170, -46) rotate(29)"
            x="50"
            y="162"
          >
            Outright Forward
          </text>
          <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="216" y="38">
            Target
          </text>
          <line stroke={theme.dashLineColor} strokeDasharray="3 0 3" x1="62" y1="43.7" x2="248" y2="43.7" />
          <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="204" y="79">
            K
          </text>
          <rect fill={themeColor} width="1.8" height="14.1" x="208" y="87.3" />
          <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="170" y="79">
            B
          </text>
          <rect fill={themeColor} width="1.8" height="14.1" x="172" y="87.3" />
          <StyledSvgPath stroke="#FF0000" strokeWidth={2} d="M241.3,159.5l-32.2-30.8l-0.2-34.1h-34.5L75.6,43.7H26.1" />
        </StyledSvgPathGroup>
      )}
    </>
  );
}
const PayoffGraphAllFwac = ({ isAccumulatorTrigger, way, theme }: PayoffGraphAllProps) => {
  const graphProps = { way, theme };

  return <>{!isAccumulatorTrigger ? <GraphFwac {...graphProps} /> : <GraphFwacWithTrigger {...graphProps} />}</>;
};

function GraphFwac({ way, theme }: PayoffGraphProps) {
  const themeColor = theme.generalColor;
  const dashLineColor = theme.dashLineColor;

  if (way === 'Buy') {
    return (
      <StyledSvgPathGroup id="forward-buy" fill={themeColor}>
        <text
          className="fw-medium"
          fontStyle="italic"
          style={{ fontSize: '.68rem' }}
          transform="translate(2, 10) rotate(-46)"
          x="50"
          y="162"
        >
          Outright Fwd
        </text>
        <line stroke={dashLineColor} strokeDasharray="3 0 3" x1="48" y1="164" x2="188" y2="26" />
        <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="80" y="79">
          K
        </text>
        <rect fill={themeColor} width="1.8" height="14.1" x="84" y="87.3" />
        <StyledSvgPath stroke="#FF0000" strokeWidth={2} d="m52.3 173.9 32.3-79.1 68.4-69 .2-.2" />
      </StyledSvgPathGroup>
    );
  }
  return (
    <StyledSvgPathGroup id="forward-sell" fill={themeColor}>
      <text
        className="fw-medium"
        fontStyle="italic"
        style={{ fontSize: '.68rem' }}
        transform="translate(121, -113) rotate(46)"
        x="50"
        y="162"
      >
        Outright Fwd
      </text>
      <line stroke={dashLineColor} strokeDasharray="3 0 3" x1="54" y1="26" x2="196" y2="164" />
      <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="155" y="79">
        K
      </text>
      <rect fill={themeColor} width="1.8" height="14.1" x="158" y="87.3" />
      <StyledSvgPath stroke="#FF0000" strokeWidth={2} d="m191.7 173.9-32.3-79.1-68.4-69-.2-.2" />
    </StyledSvgPathGroup>
  );
}

function GraphFwacWithTrigger({ way, theme }: PayoffGraphProps) {
  const themeColor = theme.generalColor;
  const dashLineColor = theme.dashLineColor;

  if (way === 'Buy') {
    return (
      <g data-e2e="fwac-with-trigger-graph-buy">
        <StyledSvgPathGroup id="forward-trigger-buy" fill={themeColor}>
          <text
            className="fw-medium"
            fontStyle="italic"
            style={{ fontSize: '.68rem' }}
            transform="translate(-72, 70) rotate(-41)"
            x="56"
            y="164"
          >
            Outright Fwd
          </text>
          <line stroke={dashLineColor} strokeDasharray="3 0 3" x1="48" y1="160" x2="196" y2="22" />
          <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="80" y="79">
            K
          </text>
          <rect fill={themeColor} width="1.8" height="14.1" x="84" y="87.3" />
          <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="142" y="112">
            trigger
          </text>
          <rect fill={themeColor} width="1.8" height="14.1" x="158" y="87.3" />
          <StyledSvgPath stroke="#FF0000" strokeWidth={2} d="M52.7 173.9 85 94.8l74.4-69.4v69.3h28.5" />
        </StyledSvgPathGroup>
      </g>
    );
  }
  return (
    <g data-e2e="fwac-with-trigger-graph-sell">
      <StyledSvgPathGroup id="forward-trigger-sell" fill={themeColor}>
        <text
          className="fw-medium"
          fontStyle="italic"
          style={{ fontSize: '.68rem' }}
          transform="translate(190, -47) rotate(44.5)"
          x="56"
          y="164"
        >
          Outright Fwd
        </text>
        <line stroke={dashLineColor} strokeDasharray="3 0 3" x1="55" y1="25" x2="202" y2="170" />
        <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="156" y="79">
          K
        </text>
        <rect fill={themeColor} width="1.8" height="14" x="158" y="87" />
        <text className="fw-medium" style={{ fontSize: '.68rem' }} fill={themeColor} x="68" y="112">
          trigger
        </text>
        <rect fill={themeColor} width="1.8" height="14" x="84" y="87" />
        <StyledSvgPath stroke="#FF0000" strokeWidth={2} d="M191.3 173.9 159 94.8 84.6 25.3v69.3H56.1" />
      </StyledSvgPathGroup>
    </g>
  );
}

interface PathProps {
  id?: string;
  strokeWidth?: number;
  d: string;
  strokeDasharray?: string;
}
interface StyledSvgPathProps extends PathProps {
  stroke: string;
}

const StyledSvgPath: React.FC<StyledSvgPathProps> = ({ stroke, strokeWidth = 1, d, strokeDasharray, ...props }) => {
  const dashArray = strokeDasharray === 'dash' ? '5' : '0';
  return <path d={d} stroke={stroke} strokeWidth={strokeWidth} strokeDasharray={dashArray} fill="none" {...props} />;
};

const StyledSvgPathGroup: React.FC<{
  id?: string;
  fill: string;
  children: React.ReactNode;
}> = ({ fill, children, ...props }) => {
  return (
    <g fill={fill} {...props}>
      {children}
    </g>
  );
};
